import { useState } from "react";
import { toast } from "sonner";
import {
  useAiSuggestSubjects,
  useAiRewriteTone,
  useAiSpamCheck,
  type AiToneInputTone,
  type AiSpamReport,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Sparkles,
  Wand2,
  ShieldAlert,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { apiErrorMessage, apiErrorStatus } from "@/lib/apiError";
import { useAiRateLimitUpdater } from "@/lib/aiRateLimit";

const TONE_OPTIONS: { value: AiToneInputTone; label: string }[] = [
  { value: "friendly", label: "Friendly" },
  { value: "formal", label: "Formal" },
  { value: "concise", label: "Concise" },
  { value: "apologetic", label: "Apologetic" },
  { value: "enthusiastic", label: "Enthusiastic" },
];

interface SubjectsButtonProps {
  bodyHtml: string;
  currentSubject: string;
  variableKeys: string[];
  onPick: (subject: string) => void;
  disabled?: boolean;
}

export function SubjectsButton({
  bodyHtml,
  currentSubject,
  variableKeys,
  onPick,
  disabled,
}: SubjectsButtonProps) {
  const [open, setOpen] = useState(false);
  const [subjects, setSubjects] = useState<string[] | null>(null);
  const mutation = useAiSuggestSubjects();
  const rateLimit = useAiRateLimitUpdater();

  const generate = async () => {
    if (!bodyHtml || bodyHtml.replace(/<[^>]+>/g, "").trim().length === 0) {
      toast.error("Add some body content before suggesting subjects");
      return;
    }
    setOpen(true);
    setSubjects(null);
    try {
      const res = await mutation.mutateAsync({
        data: {
          bodyHtml,
          currentSubject: currentSubject || null,
          variables: variableKeys,
        },
      });
      rateLimit.updateFromResponse(res);
      setSubjects(res.subjects);
    } catch (e) {
      const status = apiErrorStatus(e);
      if (status === 429) {
        rateLimit.updateFromError(e);
      }
      toast.error(
        status === 429
          ? `AI rate limit reached: ${apiErrorMessage(e, "try again later")}`
          : apiErrorMessage(e, "Subject suggestion failed"),
      );
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 shrink-0"
        onClick={generate}
        disabled={disabled || mutation.isPending}
        title="Suggest subject lines with AI"
        data-testid="button-ai-subjects"
      >
        {mutation.isPending ? (
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        ) : (
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
        )}
        Subjects
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Subject suggestions
            </DialogTitle>
            <DialogDescription>
              Pick one to use as the subject. You can edit it after.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            {subjects === null ? (
              <div className="py-8 flex items-center justify-center text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating…
              </div>
            ) : subjects.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No suggestions returned.
              </p>
            ) : (
              subjects.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    onPick(s);
                    setOpen(false);
                    toast.success("Subject applied");
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md border border-border hover:bg-muted/50 hover:border-primary/40 transition text-sm"
                  data-testid={`button-subject-suggestion-${i}`}
                >
                  {s}
                </button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ToneButtonProps {
  bodyHtml: string;
  onApply: (newBodyHtml: string) => void;
  onUndo: () => void;
  hasPrevious: boolean;
  disabled?: boolean;
}

export function ToneButton({
  bodyHtml,
  onApply,
  onUndo,
  hasPrevious,
  disabled,
}: ToneButtonProps) {
  const [open, setOpen] = useState(false);
  const [tone, setTone] = useState<AiToneInputTone>("friendly");
  const mutation = useAiRewriteTone();
  const rateLimit = useAiRateLimitUpdater();

  const apply = async () => {
    if (!bodyHtml || bodyHtml.replace(/<[^>]+>/g, "").trim().length === 0) {
      toast.error("Add some body content before rewriting tone");
      return;
    }
    try {
      const res = await mutation.mutateAsync({ data: { bodyHtml, tone } });
      rateLimit.updateFromResponse(res);
      onApply(res.bodyHtml);
      setOpen(false);
      toast.success(
        `Body rewritten in ${tone} tone — click Undo to revert`,
      );
    } catch (e) {
      const status = apiErrorStatus(e);
      if (status === 429) {
        rateLimit.updateFromError(e);
      }
      toast.error(
        status === 429
          ? `AI rate limit reached: ${apiErrorMessage(e, "try again later")}`
          : apiErrorMessage(e, "Tone rewrite failed"),
      );
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 shrink-0"
        onClick={() => setOpen(true)}
        disabled={disabled || mutation.isPending}
        title="Rewrite body tone with AI"
        data-testid="button-ai-tone"
      >
        {mutation.isPending ? (
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        ) : (
          <Wand2 className="w-3.5 h-3.5 mr-1.5" />
        )}
        Fix tone
      </Button>
      {hasPrevious && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 shrink-0"
          onClick={onUndo}
          disabled={disabled}
          title="Undo last tone rewrite"
          data-testid="button-ai-tone-undo"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          Undo tone
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-primary" />
              Rewrite tone
            </DialogTitle>
            <DialogDescription>
              The AI will rewrite the body in the selected tone. Variable
              placeholders are preserved. You can undo this with one click.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select
              value={tone}
              onValueChange={(v) => setTone(v as AiToneInputTone)}
            >
              <SelectTrigger data-testid="select-ai-tone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={apply} disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              Rewrite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface SpamCheckButtonProps {
  subject: string;
  bodyHtml: string;
  disabled?: boolean;
}

export function SpamCheckButton({
  subject,
  bodyHtml,
  disabled,
}: SpamCheckButtonProps) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<AiSpamReport | null>(null);
  const mutation = useAiSpamCheck();
  const rateLimit = useAiRateLimitUpdater();

  const run = async () => {
    if (!subject || subject.trim().length === 0) {
      toast.error("Enter a subject before running the spam check");
      return;
    }
    if (!bodyHtml || bodyHtml.replace(/<[^>]+>/g, "").trim().length === 0) {
      toast.error("Add some body content before running the spam check");
      return;
    }
    setOpen(true);
    setReport(null);
    try {
      const res = await mutation.mutateAsync({
        data: { subject, bodyHtml },
      });
      rateLimit.updateFromResponse(res);
      setReport(res);
    } catch (e) {
      const status = apiErrorStatus(e);
      if (status === 429) {
        rateLimit.updateFromError(e);
      }
      toast.error(
        status === 429
          ? `AI rate limit reached: ${apiErrorMessage(e, "try again later")}`
          : apiErrorMessage(e, "Spam check failed"),
      );
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 shrink-0"
        onClick={run}
        disabled={disabled || mutation.isPending}
        title="Check spam risk with AI"
        data-testid="button-ai-spam-check"
      >
        {mutation.isPending ? (
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        ) : (
          <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
        )}
        Spam check
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-primary" />
              Spam risk report
            </DialogTitle>
            <DialogDescription>
              Heuristic deliverability review of your draft. This is guidance,
              not a guarantee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[60vh] overflow-auto">
            {report === null ? (
              <div className="py-8 flex items-center justify-center text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing…
              </div>
            ) : (
              <>
                <div
                  className={
                    report.score === "low"
                      ? "flex items-center gap-2 rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-700 dark:text-green-300"
                      : report.score === "medium"
                        ? "flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
                        : "flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300"
                  }
                  data-testid="ai-spam-score"
                >
                  {report.score === "low" ? (
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                  )}
                  <span className="font-medium capitalize">
                    {report.score} risk
                  </span>
                  <span className="text-xs opacity-80">
                    · {report.issues.length}{" "}
                    {report.issues.length === 1 ? "issue" : "issues"}
                  </span>
                </div>
                {report.issues.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    No specific issues flagged.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {report.issues.map((issue, i) => (
                      <li
                        key={i}
                        className="rounded-md border border-border p-3 text-sm"
                        data-testid={`ai-spam-issue-${i}`}
                      >
                        <div className="text-xs uppercase tracking-wide text-muted-foreground font-mono mb-1">
                          {issue.type}
                        </div>
                        {issue.snippet && (
                          <div className="text-xs font-mono bg-muted/60 px-2 py-1 rounded mb-1 break-words">
                            {issue.snippet}
                          </div>
                        )}
                        <div className="text-foreground">
                          {issue.suggestion}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
